import { inject } from '@angular/core';
import { type Observable, pipe, switchMap, tap } from 'rxjs';
import { tapResponse } from '@ngrx/operators';
import { patchState, signalStore, withHooks, withMethods, withState } from '@ngrx/signals';
import { setError, setLoaded, setLoading, withCallState, withReset } from '@ngrx-toolkit/core';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { AuthStore } from '../../../../core';
import { TowingAttachmentsService } from './towing-attachments.service';
import { type TowingAttachment } from './towing-attachment.model';

type State = {
  damages: Array<TowingAttachment>;
  inventory: Array<TowingAttachment>;
};

const initialState: State = {
  damages: [],
  inventory: [],
};

export const TowingAttachmentsStore = signalStore(
  { providedIn: 'root' },
  withState(initialState),
  withCallState(),
  withReset(),
  withMethods((store, api = inject(TowingAttachmentsService)) => ({
    loadDamages: rxMethod<void>(
      pipe(
        tap(() => patchState(store, setLoading())),
        switchMap(() =>
          api.loadDamagesPhotos().pipe(
            tapResponse({
              next: (damages) => patchState(store, { damages }, setLoaded()),
              error: (error) => patchState(store, setError(error)),
            }),
          ),
        ),
      ),
    ),
    loadInventory: rxMethod<void>(
      pipe(
        tap(() => patchState(store, setLoading())),
        switchMap(() =>
          api.loadInventoryPhotos().pipe(
            tapResponse({
              next: (inventory) => patchState(store, { inventory }, setLoaded()),
              error: (error) => patchState(store, setError(error)),
            }),
          ),
        ),
      ),
    ),
    downloadDamage: (id: string): Observable<Blob> => {
      patchState(store, setLoading());
      return api.downloadDamagePhoto(id).pipe(
        tap({
          next: () => patchState(store, setLoaded()),
          error: (error) => patchState(store, setError(error)),
        }),
      );
    },
    downloadInventory: (id: string): Observable<Blob> => {
      patchState(store, setLoading());
      return api.downloadInventoryPhoto(id).pipe(
        tap({
          next: () => patchState(store, setLoaded()),
          error: (error) => patchState(store, setError(error)),
        }),
      );
    },
  })),
  withHooks({
    onInit(store, authStore = inject(AuthStore)) {
      authStore.logout$.subscribe(() => store.resetState());
    },
  }),
);
